/**
 * RevenueDashboard - Revenue metrics and tenant billing overview
 *
 * Features:
 * - Revenue chart (MRR over time)
 * - Top tenants by revenue
 * - Recent transactions
 * - Subscription breakdown by plan
 * - Financial export capabilities
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  BarChart3,
  TrendingUp,
  Download,
  Users,
  DollarSign,
  Calendar,
  AlertCircle,
} from 'lucide-react';

interface TenantRevenue {
  tenant_id: string;
  tenant_name: string;
  total_revenue: number;
  invoice_count: number;
  last_payment_date: string | null;
}

interface RevenueMetrics {
  current_month_revenue: number;
  last_month_revenue: number;
  growth_rate: number;
  subscription_breakdown: Array<{
    plan_name: string;
    count: number;
    revenue: number;
  }> | null;
}

export function RevenueDashboard() {
  const [timeRange, setTimeRange] = useState<'30d' | '90d' | '12m'>('30d');

  // Calculate date range from timeRange
  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    if (timeRange === '30d') start.setDate(end.getDate() - 30);
    else if (timeRange === '90d') start.setDate(end.getDate() - 90);
    else start.setFullYear(end.getFullYear() - 1);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    };
  };

  // Fetch top tenants by revenue
  const { data: topTenants, isLoading: tenantsLoading } = useQuery({
    queryKey: ['admin-top-tenants-revenue'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('calie_admin_get_top_tenants_by_revenue', {
        p_limit: 10,
      });
      if (error) throw error;
      return (data || []) as TenantRevenue[];
    },
  });

  // Fetch revenue metrics
  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['admin-revenue-metrics'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('calie_admin_get_revenue_metrics');
      if (error) throw error;
      // RPC returns array, get first row
      const row = Array.isArray(data) ? data[0] : data;
      return row as RevenueMetrics;
    },
  });

  const handleExportFinancials = async () => {
    try {
      const { start, end } = getDateRange();
      const { data, error } = await supabase.rpc('calie_admin_export_financial_report', {
        p_start_date: start,
        p_end_date: end,
      });
      if (error) throw error;

      // Convert array to CSV
      const rows = data as Array<{
        invoice_number: string;
        tenant_name: string;
        amount: number;
        status: string;
        issue_date: string;
        paid_date: string | null;
      }>;
      
      const headers = ['Invoice Number', 'Tenant', 'Amount', 'Status', 'Issue Date', 'Paid Date'];
      const csvRows = [
        headers.join(','),
        ...rows.map(r => [
          r.invoice_number,
          `"${r.tenant_name}"`,
          r.amount,
          r.status,
          r.issue_date,
          r.paid_date || ''
        ].join(','))
      ];
      const csv = csvRows.join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `financial-report-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success('Financial report exported successfully');
    } catch (error) {
      toast.error(`Failed to export report: ${(error as Error).message}`);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          Revenue Analytics
        </h2>
        <div className="flex gap-2">
          <Select value={timeRange} onValueChange={(value) => setTimeRange(value as typeof timeRange)}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
              <SelectItem value="12m">Last 12 Months</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleExportFinancials} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Revenue Growth Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Revenue Growth</CardTitle>
          <CardDescription>Month-over-month revenue comparison</CardDescription>
        </CardHeader>
        <CardContent>
          {metricsLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : metrics ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Current Month</p>
                <p className="text-2xl font-bold">{formatCurrency(metrics.current_month_revenue || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Month</p>
                <p className="text-2xl font-bold">{formatCurrency(metrics.last_month_revenue || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Growth Rate</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold">
                    {(metrics.growth_rate || 0) >= 0 ? '+' : ''}
                    {(metrics.growth_rate || 0).toFixed(1)}%
                  </p>
                  <TrendingUp
                    className={`h-5 w-5 ${
                      (metrics.growth_rate || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-2" />
              <p>No revenue data available</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscription Breakdown */}
      {metrics && metrics.subscription_breakdown && metrics.subscription_breakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Subscriptions by Plan</CardTitle>
            <CardDescription>Active subscriptions distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {metrics.subscription_breakdown.map((item) => (
                <div key={item.plan_name} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{item.plan_name}</Badge>
                    <span className="text-sm text-muted-foreground">
                      {item.count} {item.count === 1 ? 'tenant' : 'tenants'}
                    </span>
                  </div>
                  <div className="text-sm font-medium">{formatCurrency(item.revenue || 0)}/mo</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Tenants by Revenue */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            Top Tenants by Revenue
          </CardTitle>
          <CardDescription>Highest paying customers</CardDescription>
        </CardHeader>
        <CardContent>
          {tenantsLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : topTenants && topTenants.length > 0 ? (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tenant</TableHead>
                      <TableHead>Total Revenue</TableHead>
                      <TableHead>Invoices</TableHead>
                      <TableHead>Last Payment</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topTenants.map((tenant) => (
                      <TableRow key={tenant.tenant_id}>
                        <TableCell className="font-medium">{tenant.tenant_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            {formatCurrency(tenant.total_revenue)}
                          </div>
                        </TableCell>
                        <TableCell>{tenant.invoice_count}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(tenant.last_payment_date)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {topTenants.map((tenant) => (
                  <Card key={tenant.tenant_id} className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold">{tenant.tenant_name}</h4>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Revenue:</span>
                          <div className="font-medium">{formatCurrency(tenant.total_revenue)}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Invoices:</span>
                          <div className="font-medium">{tenant.invoice_count}</div>
                        </div>
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Last Payment:</span>
                          <div>{formatDate(tenant.last_payment_date)}</div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No tenant data available</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
